import React from "react";
import ShopBusinessLinks from "./ShopBusinessLinks";
import {
  formatShopFitReasons,
  formatShopLocation,
  formatShopMatchScore,
  formatShopRating,
  getShopBadges,
  buildShopServicesLabel,
  getShopBusinessLinks,
} from "./shopDisplayHelpers";
import { formatDurationFromMinutes, formatLastActive, formatMemberSince } from "./shopTrustFormatting";
import { SHOP_PIN_COLORS } from "./shopMapPopupCard";

export default function ShopMatchCard({
  shop,
  index,
  effectiveMake = "",
  mapsUrl = "",
  selectable = false,
  selected = false,
  onToggleSelect,
  showFavoriteButton = false,
  isFavorite = false,
  onToggleFavorite,
}) {
  const badges = getShopBadges(shop, effectiveMake);
  const matchScore = formatShopMatchScore(shop);
  const fitReasons = formatShopFitReasons(shop);
  const servicesLabel = buildShopServicesLabel(shop);
  const rating = formatShopRating(shop);
  const hasLinks = getShopBusinessLinks(shop, mapsUrl).length > 0;
  const isMobile = !!shop?.is_mobile_mechanic;
  // Left-border accent matches the map legend / pin color: violet for mobile mechanics,
  // cyan for fixed shops - so the card list reads consistently with the map.
  const accentColor = isMobile ? SHOP_PIN_COLORS.mobile : SHOP_PIN_COLORS.shop;
  const serviceArea = isMobile
    ? `Serves ~${shop.mobile_service_radius_miles || 25} mi around ${shop.zip_code || "their area"}`
    : "";

  // Car-request selection: the whole card is the toggle (no checkbox), so the content uses
  // the full card width. Inner links/buttons stopPropagation so they don't also toggle it.
  const isSelected = selectable && selected;
  const bgClass = isSelected
    ? "bg-blue-50 dark:bg-blue-950/40"
    : isMobile
    ? "bg-violet-50 dark:bg-violet-950/40"
    : "bg-white dark:bg-gray-800";
  const selectProps = selectable
    ? {
        role: "button",
        tabIndex: 0,
        "aria-pressed": selected,
        onClick: onToggleSelect,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggleSelect?.();
          }
        },
      }
    : {};
  const stop = (e) => e.stopPropagation();

  return (
    <div
      {...selectProps}
      className={`relative flex flex-col gap-2 border border-l-4 rounded-lg p-3 transition ${bgClass} ${
        selectable ? "cursor-pointer" : ""
      } ${isSelected ? "ring-2 ring-blue-600 dark:ring-blue-400" : ""}`}
      style={{ borderLeftColor: accentColor }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          {shop.shop_logo_url ? (
            <img
              src={shop.shop_logo_url}
              alt="Shop logo"
              className="h-12 w-12 sm:h-16 sm:w-16 rounded border object-contain bg-white dark:bg-gray-800 flex-shrink-0"
            />
          ) : null}
          <div className="min-w-0">
            <div className="font-medium text-gray-900 dark:text-gray-100 line-clamp-2">
              {index != null ? `${index}. ` : ""}{shop.shop_name}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-300">{formatShopLocation(shop)}</div>
            {serviceArea ? (
              <div className="text-xs font-medium text-violet-700 dark:text-violet-300">{serviceArea}</div>
            ) : null}
            {rating ? (
              shop.business_google_profile_url ? (
                <a
                  href={shop.business_google_profile_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={stop}
                  className="mt-0.5 inline-flex items-center gap-1 whitespace-nowrap text-xs text-gray-700 dark:text-gray-200 hover:underline"
                  title="View this shop's Google reviews"
                >
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0 text-amber-500" fill="currentColor" aria-hidden="true">
                    <path d="M12 2l2.9 6.3 6.9.7-5.1 4.6 1.4 6.8L12 17.8 5.9 20.4l1.4-6.8L2.2 9l6.9-.7z" />
                  </svg>
                  <span className="font-semibold">{rating.rating}</span>
                  <span className="text-gray-500 dark:text-gray-400">({rating.count}) on Google</span>
                </a>
              ) : (
                <span className="mt-0.5 inline-flex items-center gap-1 whitespace-nowrap text-xs text-gray-700 dark:text-gray-200">
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0 text-amber-500" fill="currentColor" aria-hidden="true">
                    <path d="M12 2l2.9 6.3 6.9.7-5.1 4.6 1.4 6.8L12 17.8 5.9 20.4l1.4-6.8L2.2 9l6.9-.7z" />
                  </svg>
                  <span className="font-semibold">{rating.rating}</span>
                  <span className="text-gray-500 dark:text-gray-400">({rating.count}) on Google</span>
                </span>
              )
            ) : null}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          {isSelected ? (
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-white shadow" aria-hidden="true">
              <i className="not-italic fi fi-rr-check text-xs" />
            </span>
          ) : null}
          {/* Both are null when there is no search to score against - e.g. the card pinned in an
              open thread - and the card simply omits them, matching the rating line above. */}
          {matchScore ? (
            <div className="text-sm font-semibold text-blue-700 dark:text-blue-300 whitespace-nowrap">{matchScore}</div>
          ) : null}
        </div>
      </div>

      {fitReasons ? (
        <div className="text-xs text-gray-600 dark:text-gray-300">{fitReasons}</div>
      ) : null}

      <div className="text-xs text-gray-700 dark:text-gray-200 space-y-0.5">
        <div>{formatMemberSince(shop.member_since)}</div>
        <div>{formatLastActive(shop.last_active_at)}</div>
        <div>Completed work orders: {Number(shop.completed_work_orders_count || 0)}</div>
        <div>Avg first response: {formatDurationFromMinutes(shop.avg_first_response_minutes)}</div>
      </div>

      {badges.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          {badges.map((badge) => (
            <span key={badge.key} className="px-2 py-0.5 rounded-full border text-xs">{badge.label}</span>
          ))}
        </div>
      ) : null}

      {servicesLabel ? (
        <div className="text-xs text-gray-600 dark:text-gray-300">{servicesLabel}</div>
      ) : null}

      {hasLinks || showFavoriteButton ? (
        <div className="flex items-end justify-between gap-2">
          <div className="min-w-0" onClick={stop}>
            <ShopBusinessLinks shop={shop} mapsUrl={mapsUrl} className="" />
          </div>
          {showFavoriteButton ? (
            <button
              type="button"
              onClick={(e) => {
                stop(e);
                onToggleFavorite?.();
              }}
              className={`shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full border bg-white dark:bg-gray-800 ${
                isFavorite ? "text-red-600 dark:text-red-400" : "text-gray-600 dark:text-gray-300"
              }`}
              title={isFavorite ? "Unfavorite shop" : "Favorite shop"}
              aria-label={isFavorite ? "Unfavorite shop" : "Favorite shop"}
            >
              <i className={`not-italic text-base ${isFavorite ? "fi fi-sr-heart" : "fi fi-rs-heart fi-rr-heart"}`} />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
